import React, { useState, useEffect } from 'react'
import { Search, X } from 'lucide-react'
import { useAppSelector } from '@/store/hooks'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { TaskService } from '@/api/generated/services/TaskService'

interface Task {
  id: string
  name: string
  assignee?: {
    id: string
    name: string
    email: string
  }
  department?: {
    id: string
    name: string
  }
  orgHierarchy?: string[]
}

interface TaskSelectModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (task: Task) => void
  parentCategoryName?: string
}

const TaskSelectModal: React.FC<TaskSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  parentCategoryName
}) => {
  const currentWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)

  // Fetch tasks when modal opens
  useEffect(() => {
    if (isOpen && currentWorkspace?.id) {
      fetchTasks()
    }
  }, [isOpen, currentWorkspace?.id, searchQuery])

  const fetchTasks = async () => {
    if (!currentWorkspace?.id) return

    try {
      setLoading(true)
      setError(null)

      const response = await TaskService.appsTaskViewsListTasks({
        workspaceId: currentWorkspace.id,
        q: searchQuery.trim() || undefined,
        page: 1,
        perPage: 100
      })

      // Transform response data to Task interface
      const taskData = Array.isArray(response.data) ? response.data : []
      const transformedTasks: Task[] = taskData.map((task: any) => ({
        id: task.id,
        name: task.name || 'Unnamed Task',
        assignee: task.assignee ? {
          id: task.assignee.id,
          name: task.assignee.name,
          email: task.assignee.email
        } : undefined,
        department: task.department_id ? {
          id: task.department_id,
          name: task.department_name || ''
        } : undefined,
        orgHierarchy: task.orgHierarchy || []
      }))

      setTasks(transformedTasks)
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
      setError('세부실행과제 목록을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    fetchTasks()
  }

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task)
  }

  const handleConfirm = () => {
    if (selectedTask) {
      onSelect(selectedTask)
    }
  }

  const handleClose = () => {
    setSelectedTask(null)
    setSearchQuery('')
    setTasks([])
    onClose()
  }

  if (!isOpen) return null

  const getHierarchyDisplay = (task: Task) => {
    if (!task.orgHierarchy || task.orgHierarchy.length <= 1) return '-'
    // Remove the task name itself (last item) from hierarchy
    return task.orgHierarchy.slice(0, -1).join(' > ')
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div 
        className="bg-white rounded-lg shadow-lg"
        style={{
          width: '800px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderBottomColor: designTokens.colors.gray[1] }}
        >
          <div>
            <h2 style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-medium'][0],
              fontWeight: 700,
              color: designTokens.colors.gray[6]
            }}>
              세부실행과제 선택
            </h2>
            {parentCategoryName && (
              <p style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-small'][0],
                color: designTokens.colors.gray[4],
                marginTop: '4px'
              }}>
                연결 대상: {parentCategoryName}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <div
                className="flex items-center gap-2 px-3 py-2 border rounded"
                style={{
                  backgroundColor: designTokens.colors.white,
                  borderColor: designTokens.colors.gray[1]
                }}
              >
                <Search className="w-4 h-4" style={{ color: designTokens.colors.gray[3] }} />
                <input
                  type="text"
                  placeholder="세부실행과제명으로 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent outline-none"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6]
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                />
              </div>
            </div>
            <Button
              color="dark"
              size="medium"
              onClick={handleSearch}
              disabled={loading}
            >
              검색
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: designTokens.colors.gray[4]
              }}>
                세부실행과제 목록을 불러오는 중...
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: designTokens.colors.error.DEFAULT
              }}>
                {error}
              </span>
            </div>
          )}

          {!loading && !error && tasks.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: designTokens.colors.gray[4]
              }}>
                {searchQuery ? '검색 결과가 없습니다.' : '사용 가능한 세부실행과제가 없습니다.'}
              </span>
            </div>
          )}

          {!loading && !error && tasks.length > 0 && (
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <div className="p-4">
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedTask?.id === task.id ? 'border-2' : 'hover:bg-gray-50'
                      }`}
                      style={{
                        borderColor: selectedTask?.id === task.id 
                          ? designTokens.colors.primary.DEFAULT 
                          : designTokens.colors.gray[1],
                        backgroundColor: selectedTask?.id === task.id 
                          ? designTokens.colors.primary.light + '10' 
                          : 'transparent'
                      }}
                      onClick={() => handleTaskSelect(task)}
                    >
                      <div className="space-y-2">
                        {/* Task Name */}
                        <div style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          fontWeight: 600,
                          color: designTokens.colors.gray[6]
                        }}>
                          {task.name}
                        </div>
                        
                        {/* Task Details */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-small'][0],
                              color: designTokens.colors.gray[4]
                            }}>
                              담당자: {task.assignee?.name || '-'}
                            </span>
                          </div>
                          <div>
                            <span style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-small'][0],
                              color: designTokens.colors.gray[4]
                            }}>
                              부서: {task.department?.name || '-'}
                            </span>
                          </div>
                        </div>

                        {/* Hierarchy */}
                        <div>
                          <span style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-small'][0],
                            color: designTokens.colors.gray[4]
                          }}>
                            계층: {getHierarchyDisplay(task)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-2 p-6 border-t"
          style={{ borderTopColor: designTokens.colors.gray[1] }}
        >
          <Button
            color="white"
            size="medium"
            onClick={handleClose}
          >
            취소
          </Button>
          <Button
            color="dark"
            size="medium"
            onClick={handleConfirm}
            disabled={!selectedTask}
          >
            선택
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TaskSelectModal